import React from 'react';
import { Box, Container, Typography, alpha, useTheme } from '@mui/material';
import { ArrowLeft, Moon, Sun } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logoMark from '../../img/logo-branco.png';
import { useColorMode } from '../../hooks/useColorMode';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import { MobileAuthPage } from '../../component/auth/MobileAuth';

export const LEGAL_LAST_UPDATE = '07/10/2026';

// Destaca placeholders jurídicos ainda não preenchidos, ex.: "[RAZÃO SOCIAL]".
function renderText(text) {
  return String(text).split(/(\[[^\]]+\])/g).map((part, index) => (
    /^\[[^\]]+\]$/.test(part)
      ? (
        <Box key={index} component="mark" sx={{ backgroundColor: (t) => alpha(t.palette.warning.main, 0.25), color: 'inherit', borderRadius: '4px', px: '4px' }}>
          {part}
        </Box>
      )
      : <React.Fragment key={index}>{part}</React.Fragment>
  ));
}

/**
 * Layout de leitura das páginas legais públicas (Termos de Uso e Política de
 * Privacidade). `sections`: [{ id, title, blocks: (string | { list: string[] })[] }].
 */
export default function LegalLayout({ title, intro, sections }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, toggleMode } = useColorMode();
  const isMobileShell = useIsMobileShell();

  // Aberta em nova aba não há histórico anterior: volta para a landing.
  const handleBack = () => {
    if (location.key !== 'default') navigate(-1);
    else navigate('/');
  };

  if (isMobileShell) {
    // prototipos-mobile/auth/termos.html e privacidade.html
    return (
      <MobileAuthPage title={title} onBack={handleBack}>
        <Box sx={{ fontSize: 11.5, color: 'text.disabled' }}>Atualizado em {LEGAL_LAST_UPDATE}</Box>
        {intro && <Typography sx={{ color: 'text.secondary', lineHeight: 1.6, mt: '18px' }}>{renderText(intro)}</Typography>}
        {sections.map((section, index) => (
          <Box component="section" key={section.id}>
            <Typography component="h3" sx={{ fontSize: 15, fontWeight: 800, m: '18px 0 6px', lineHeight: 1.4 }}>{index + 1}. {section.title}</Typography>
            {section.blocks.map((block, blockIndex) => (
              typeof block === 'string'
                ? <Typography key={blockIndex} sx={{ color: 'text.secondary', lineHeight: 1.6, mb: '8px' }}>{renderText(block)}</Typography>
                : block.link
                  ? <Typography key={blockIndex} sx={{ lineHeight: 1.6, mb: '8px' }}><Box component="a" href={block.link.href} target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main', fontWeight: 700 }}>{block.link.label}</Box></Typography>
                  : <Box key={blockIndex} component="ul" sx={{ m: '0 0 8px', pl: '20px', color: 'text.secondary', lineHeight: 1.6 }}>{block.list.map((item, itemIndex) => <li key={itemIndex}>{renderText(item)}</li>)}</Box>
            ))}
          </Box>
        ))}
      </MobileAuthPage>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', color: 'text.primary' }}>
      <Box
        component="header"
        sx={{
          position: 'sticky', top: 0, zIndex: 10, borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: alpha(theme.palette.background.paper, 0.85), backdropFilter: 'blur(10px)',
        }}
      >
        <Container maxWidth="md" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64, gap: 2 }}>
          <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', gap: 1.2, textDecoration: 'none', color: 'inherit' }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '11px', backgroundImage: theme.palette.gradient.brand, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box component="img" src={logoMark} alt="FlowVendas" sx={{ width: 21, height: 21, display: 'block', objectFit: 'contain' }} />
            </Box>
            <Typography sx={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.01em' }}>
              Flow<Box component="span" sx={{ color: 'brand2.main' }}>Vendas</Box>
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="button"
              type="button"
              onClick={toggleMode}
              title="Alternar tema"
              sx={{
                width: 38, height: 38, borderRadius: '12px', border: `1px solid ${theme.palette.divider}`, backgroundColor: 'transparent',
                color: 'text.secondary', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                '&:hover': { color: 'primary.main', borderColor: 'primary.main' },
              }}
            >
              {mode === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </Box>
            <Box
              component="button"
              type="button"
              onClick={handleBack}
              sx={{
                height: 38, px: 1.75, borderRadius: '12px', border: `1px solid ${theme.palette.divider}`, backgroundColor: 'transparent',
                color: 'text.primary', display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', fontSize: 13.5, fontWeight: 600, fontFamily: 'inherit',
                '&:hover': { color: 'primary.main', borderColor: 'primary.main' },
              }}
            >
              <ArrowLeft size={15} /> Voltar
            </Box>
          </Box>
        </Container>
      </Box>

      <Container component="main" maxWidth={false} sx={{ maxWidth: 792, py: { xs: 4, md: 7 } }}>
        <Typography component="h1" sx={{ fontSize: { xs: 28, md: 36 }, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2, mb: 1 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 3 }}>
          Última atualização: {LEGAL_LAST_UPDATE}
        </Typography>
        {intro && (
          <Typography sx={{ fontSize: 15, lineHeight: 1.7, color: 'text.secondary', mb: 3 }}>
            {renderText(intro)}
          </Typography>
        )}

        {sections.map((section, index) => (
          <Box component="section" key={section.id} sx={{ mb: 4.5 }}>
            <Typography component="h2" sx={{ fontSize: { xs: 19, md: 21 }, fontWeight: 800, letterSpacing: '-0.01em', mb: 1.5 }}>
              {index + 1}. {section.title}
            </Typography>
            {section.blocks.map((block, blockIndex) => (
              typeof block === 'string'
                ? <Typography key={blockIndex} sx={{ fontSize: 15, lineHeight: 1.75, color: 'text.secondary', mb: 1.5 }}>{renderText(block)}</Typography>
                : block.link
                  ? (
                    <Typography key={blockIndex} sx={{ fontSize: 15, lineHeight: 1.75, color: 'text.secondary', mb: 1.5 }}>
                      <Box component="a" href={block.link.href} target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main', fontWeight: 600 }}>{block.link.label}</Box>
                    </Typography>
                  )
                  : (
                  <Box key={blockIndex} component="ul" sx={{ m: 0, mb: 1.5, pl: 3, fontSize: 15, lineHeight: 1.75, color: 'text.secondary' }}>
                    {block.list.map((item, itemIndex) => <li key={itemIndex}>{renderText(item)}</li>)}
                  </Box>
                )
            ))}
          </Box>
        ))}
      </Container>

      <Box component="footer" sx={{ borderTop: `1px solid ${theme.palette.divider}`, py: 3 }}>
        <Container maxWidth="md" sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 1.5, fontSize: 13, color: 'text.secondary' }}>
          <Box>© {new Date().getFullYear()} FlowVendas. Todos os direitos reservados.</Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box component={Link} to="/termos-de-uso" sx={{ color: 'inherit', '&:hover': { color: 'primary.main' } }}>Termos de Uso</Box>
            <Box component={Link} to="/politica-de-privacidade" sx={{ color: 'inherit', '&:hover': { color: 'primary.main' } }}>Política de Privacidade</Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
